import { useEffect, useState } from 'react';
import {
  GraduationCap,
  Trash,
  Check,
  X,
  PencilSimple,
  WarningCircle,
  Clock,
  CheckCircle,
  ShieldWarning,
  Question,
  ArrowClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import type { LearnedRule, PromptApi, PromptResponse } from '../../../../services/dashboard.service';
import { promptApi, type AiSettingsKind } from './promptApi';
import { EmptyState } from '../../../../components/ui/EmptyState';

const formatTimestamp = (iso: string | undefined | null): string => {
  if (!iso) return 'unknown date';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

/** Why an inactive rule cannot fire right now, in reviewer-facing prose. */
const inactiveReason = (rule: LearnedRule): string => {
  if (rule.waiting_on) {
    const { category, sub_category } = rule.waiting_on;
    const target = sub_category ? `${sub_category}` : category;
    return `waiting on approval of ${target}`;
  }
  if (rule.deactivated_reason === 'cap') {
    return 'deactivated to stay within the 25-rule cap';
  }
  return rule.deactivated_reason || 'inactive';
};

/**
 * The replay-check status chip: green once a rule has been re-run against
 * known-correct past cases and still agrees with them, amber when it broke
 * one, grey when the check never finished (a timeout or an error — never
 * treated as a pass). A rule with no `replay_status` (an amend/reinforce,
 * which never gates a new rule) shows no chip at all.
 */
const ReplayStatusChip = ({ rule }: { rule: LearnedRule }) => {
  if (!rule.replay_status) return null;

  if (rule.replay_status === 'passed') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
        <CheckCircle size={11} weight="fill" />
        {rule.active ? 'Checked, activated' : 'Checked, passed'}
      </span>
    );
  }

  if (rule.replay_status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
        <ShieldWarning size={11} weight="fill" />
        Held: broke a past case
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
      <Question size={11} weight="bold" />
      Check did not finish
    </span>
  );
};

const RuleCard = ({
  rule,
  api,
  onSaved,
}: {
  rule: LearnedRule;
  api: PromptApi;
  onSaved: (data: PromptResponse) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(rule.text);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingActivate, setConfirmingActivate] = useState(false);

  // A rule that has not been checked, or that broke a past case, cannot be
  // switched on with the plain toggle — a failed rule only goes live through
  // "Activate anyway", behind a confirm naming the case it broke.
  const canActivate = !rule.waiting_on && rule.replay_status !== 'failed' && rule.replay_status !== 'stalled';
  const canRecheck = rule.replay_status === 'failed' || rule.replay_status === 'stalled';

  const saveText = async () => {
    const trimmed = text.trim();
    if (!trimmed || trimmed === rule.text) {
      setEditing(false);
      setText(rule.text);
      return;
    }
    setBusy(true);
    try {
      const data = await api.patchRule(rule.id, { text: trimmed });
      onSaved(data);
      setEditing(false);
      toast.success('Rule updated.');
    } catch (e: any) {
      toast.error(e.message || 'Failed to update the rule.');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    if (!rule.active && rule.waiting_on) {
      // The backend rejects this too, but naming it here saves a round trip.
      toast.error('This rule names a category that is still pending approval.');
      return;
    }
    if (!rule.active && !canActivate) {
      // Failed or stalled: the plain switch is disabled for this, but guard anyway.
      toast.error('This rule has not passed its replay check. Use "Activate anyway" or re-run the check.');
      return;
    }
    setBusy(true);
    try {
      const data = await api.patchRule(rule.id, { active: !rule.active });
      onSaved(data);
      toast.success(rule.active ? 'Rule deactivated.' : 'Rule activated.');
    } catch (e: any) {
      toast.error(e.message || 'Failed to update the rule.');
    } finally {
      setBusy(false);
    }
  };

  const recheck = async () => {
    setBusy(true);
    try {
      const data = await api.recheckRule(rule.id);
      onSaved(data);
      toast.success('Replay check re-run.');
    } catch (e: any) {
      toast.error(e.message || 'Failed to re-run the check.');
    } finally {
      setBusy(false);
    }
  };

  const activateAnyway = async () => {
    setBusy(true);
    try {
      const data = await api.patchRule(rule.id, { active: true });
      onSaved(data);
      toast.success('Rule activated.');
    } catch (e: any) {
      toast.error(e.message || 'Failed to activate the rule.');
    } finally {
      setBusy(false);
      setConfirmingActivate(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const data = await api.deleteRule(rule.id);
      onSaved(data);
      toast.success('Rule deleted.');
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete the rule.');
    } finally {
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <div
      className={`border rounded-2xl p-4 space-y-3 transition-smooth ${
        rule.active ? 'border-slate-100 bg-white' : 'border-slate-100 bg-slate-50/70 opacity-75'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-1.5">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveText();
                  if (e.key === 'Escape') { setEditing(false); setText(rule.text); }
                }}
                className="flex-1 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-4 focus:ring-accent/10 outline-none rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-800"
              />
              <button
                onClick={() => void saveText()}
                disabled={busy}
                className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
                title="Save"
              >
                <Check size={14} weight="bold" />
              </button>
              <button
                onClick={() => { setEditing(false); setText(rule.text); }}
                disabled={busy}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
                title="Cancel"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          ) : (
            <p className="text-xs font-semibold text-slate-800 leading-snug">{rule.text}</p>
          )}

          {rule.replay_status && (
            <div className="flex items-center gap-2">
              <ReplayStatusChip rule={rule} />
              {rule.replay_checked_at && (
                <span className="text-[10px] text-slate-400">
                  checked {formatTimestamp(rule.replay_checked_at)}
                </span>
              )}
            </div>
          )}

          {rule.replay_status === 'failed' && rule.replay_broke && (
            <p className="text-[11px] text-amber-800 leading-relaxed bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2">
              Would have reclassified{' '}
              <strong>{rule.replay_broke.project_name || 'a past case'}</strong> from{' '}
              <code className="font-mono text-[10px] bg-white/70 px-1 py-0.5 rounded">
                {rule.replay_broke.expected}
              </code>{' '}
              to{' '}
              <code className="font-mono text-[10px] bg-white/70 px-1 py-0.5 rounded">
                {rule.replay_broke.got}
              </code>
              .
            </p>
          )}

          {rule.diagnosis && (
            <p className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-2">
              <span className="font-bold text-slate-600">Diagnosis: </span>
              {rule.diagnosis}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-400 font-medium pt-0.5">
            <span>
              {rule.occurrences} occurrence{rule.occurrences === 1 ? '' : 's'}
            </span>
            {rule.created_from && (
              <span className="font-mono">
                {rule.created_from.ai || '—'} <span className="text-slate-300">→</span> {rule.created_from.correct || '—'}
              </span>
            )}
            <span>{formatTimestamp(rule.created_at)}</span>
          </div>

          {!rule.active && rule.replay_status !== 'failed' && rule.replay_status !== 'stalled' && (
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-600">
              <Clock size={12} weight="bold" />
              {inactiveReason(rule)}
            </div>
          )}

          {canRecheck && (
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => void recheck()}
                disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-slate-600 text-[10px] font-bold hover:bg-slate-50"
              >
                <ArrowClockwise size={12} weight="bold" />
                Re-run check
              </button>

              {rule.replay_status === 'failed' && !rule.active && !rule.waiting_on && (
                confirmingActivate ? (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-amber-700 font-semibold max-w-[220px]">
                      This will reclassify{' '}
                      {rule.replay_broke?.project_name || 'a past case'} incorrectly. Activate anyway?
                    </span>
                    <button
                      onClick={() => void activateAnyway()}
                      disabled={busy}
                      className="px-2 py-1 rounded-lg bg-amber-500 text-white text-[10px] font-bold hover:bg-amber-600 shrink-0"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmingActivate(false)}
                      disabled={busy}
                      className="px-2 py-1 rounded-lg text-slate-400 text-[10px] font-bold hover:bg-slate-100 shrink-0"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingActivate(true)}
                    disabled={busy}
                    className="px-2 py-1 rounded-lg border border-amber-200 text-amber-700 text-[10px] font-bold hover:bg-amber-50"
                  >
                    Activate anyway
                  </button>
                )
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              disabled={busy}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              title="Edit rule text"
            >
              <PencilSimple size={14} weight="bold" />
            </button>
          )}

          <label
            className={`relative inline-flex items-center ${canActivate || rule.active ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}
            title={
              !canActivate && !rule.active
                ? rule.waiting_on
                  ? 'This rule names a category still pending approval and cannot be activated.'
                  : rule.replay_status === 'failed'
                    ? 'This rule broke a past case. Use "Activate anyway" to override.'
                    : 'The replay check has not finished. Re-run it before activating.'
                : rule.active ? 'Deactivate' : 'Activate'
            }
          >
            <input
              type="checkbox"
              checked={rule.active}
              disabled={busy || (!rule.active && !canActivate)}
              onChange={() => void toggleActive()}
              className="sr-only peer"
            />
            <div className="w-8 h-4 bg-slate-200 peer-checked:bg-accent rounded-full transition-colors peer-disabled:opacity-50" />
            <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4" />
          </label>

          {confirmingDelete ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => void remove()}
                disabled={busy}
                className="px-2 py-1 rounded-lg bg-rose-500 text-white text-[10px] font-bold hover:bg-rose-600"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                disabled={busy}
                className="px-2 py-1 rounded-lg text-slate-400 text-[10px] font-bold hover:bg-slate-100"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
              title="Delete rule"
            >
              <Trash size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const RulesTab = ({ kind = 'project' }: { kind?: AiSettingsKind }) => {
  const api = promptApi(kind);
  const [rules, setRules] = useState<LearnedRule[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getPrompt();
      setRules(data.learned_rules || []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load learned rules.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const applyResponse = (data: PromptResponse) => setRules(data.learned_rules || []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex justify-center items-center h-48 text-slate-400 text-sm">Loading rules…</div>
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <EmptyState
          type="general"
          title="No rules yet"
          description={kind === 'proposal'
            ? 'No rules yet. They are written when someone changes what the AI filled in while creating a proposal.'
            : 'No rules yet. They are written when someone uses Correct AI on a project.'}
        />
      </div>
    );
  }

  const active = rules.filter((r) => r.active);
  const inactive = rules.filter((r) => !r.active);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-6">
      <div className="flex items-center gap-2">
        <GraduationCap size={18} className="text-accent" weight="fill" />
        <h2 className="text-sm font-bold text-slate-800">Learned rules</h2>
        <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
          {active.length} active
        </span>
      </div>

      <div className="space-y-3">
        {active.map((rule) => (
          <RuleCard key={rule.id} rule={rule} api={api} onSaved={applyResponse} />
        ))}
      </div>

      {inactive.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-1.5 pt-3">
            <WarningCircle size={13} className="text-slate-400" weight="bold" />
            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Inactive ({inactive.length})
            </h3>
          </div>
          <div className="space-y-3">
            {inactive.map((rule) => (
              <RuleCard key={rule.id} rule={rule} api={api} onSaved={applyResponse} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
